import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import useInapiApplicationService from '../../../app/services/hooks/useInapiApplicationService';
import useBrandRegistrationVerificationService from '../../../app/services/hooks/useBrandRegistrationVerificationService';
import { StagesData } from '../../../app/services/hooks/useBrandRegistrationStagesService';
import ButtonGeneral from '../../../components/buttons/ButtonGeneral';
import InputField from '../../../components/InputField';
import FileUpload from '../../../components/FileUpload';
import useHandleErrorFields from '../../../hooks/useHandleErrorFields';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import DefaultCard from '../../../components/default/DefaultCard';
import { safeRedirectUrl } from '../../Organization/types';

interface InapiApplicationStepProps {
    brandRegistrationId: number;
    stagesData: StagesData | null;
    isEditable: boolean;
    canSign?: boolean;
    canConfirm?: boolean;
    onUpdate: () => void;
}

interface InapiApplicationData {
    id: number;
    verification_id: number;
    person_type: string;
    legal_representative_first_name: string;
    legal_representative_paternal_last_name: string;
    legal_representative_maternal_last_name: string;
    legal_representative_rut: string;
    email: string;
    phone: string;
    nationality_country: string;
    company_name: string;
    company_rut: string;
    // Nombres que devuelve el backend (get_inapi_application_view)
    residence_country_name: string;
    residence_region_name: string;
    residence_commune_name: string;
    residence_city: string;
    address: string;
    postal_code: string;
    power_city: string;
    power_day: number | null;
    power_month: number | null;
    power_year: number | null;
    legal_representative_full_name: string;
    power_address: string;
    power_municipality: string;
    power_residence_city: string;
    power_document_url: string | null;
    signature_image_url: string | null;
    inapi_submission_document_url: string | null;
    status: string;
    inapi_application_number: string | null;
}

const InapiApplicationStep: React.FC<InapiApplicationStepProps> = ({ 
    brandRegistrationId, 
    stagesData,
    isEditable,
    canSign = false,
    canConfirm = false,
    onUpdate 
}) => {
    const history = useHistory();
    const { getInapiApplication, fetchingGetApplication, downloadPowerDocument, updateInapiNumber, fetchingUpdateInapiNumber, confirmInapiApplication, fetchingConfirmApplication, generateSignatureLink, fetchingSignatureLink, sendSignatureEmail, fetchingSendSignatureEmail } = useInapiApplicationService();
    const { requestConfirmation } = useReactConfirmAlert();
    const { getVerificationByBrandRegistration } = useBrandRegistrationVerificationService();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    
    const [inapiData, setInapiData] = useState<InapiApplicationData | null>(null);
    const [verificationId, setVerificationId] = useState<number | null>(null);
    const [verificationData, setVerificationData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [inapiNumber, setInapiNumber] = useState<string>('');
    const [isEditingNumber, setIsEditingNumber] = useState(false);
    const [submissionDocument, setSubmissionDocument] = useState<File | null>(null);

    useEffect(() => {
        // Verificar si el solicitante debe pagar antes de cargar datos
        if (stagesData?.summary.is_applicant && 
            stagesData.summary.verification_payment_status === 'pending' && 
            stagesData.summary.payment_url) {
            // Redirigir automáticamente a la URL de pago
            window.location.href = safeRedirectUrl(stagesData.summary.payment_url);
            return;
        }
        
        loadInapiData();
    }, [brandRegistrationId, stagesData]);

    const loadInapiData = () => {
        // Primero obtener la verificación
        getVerificationByBrandRegistration(brandRegistrationId, {
            onSuccess: (response: any) => {
                if (response.data && response.data.verification) {
                    const verification = response.data.verification;
                    setVerificationId(verification.id);
                    setVerificationData(verification); // Guardar datos completos de verificación
                    
                    // Verificar si el pago está pendiente y el usuario es el solicitante
                    if (stagesData?.summary.is_applicant && 
                        verification.payment_status === 'pending' && 
                        stagesData.summary.payment_url) {
                        // Redirigir a la URL de pago
                        window.location.href = safeRedirectUrl(stagesData.summary.payment_url);
                        return;
                    }
                    
                    // Luego obtener la aplicación INAPI
                    getInapiApplication(verification.id, {
                        onSuccess: (inapiResponse: any) => {
                            if (inapiResponse.data && inapiResponse.data.inapi_application) {
                                const appData = inapiResponse.data.inapi_application;
                                setInapiData(appData);
                                setInapiNumber(appData.inapi_application_number || '');
                            }
                            setLoading(false);
                        },
                        onError: () => {
                            setLoading(false);
                        }
                    });
                } else {
                    setLoading(false);
                    toast.info('No se encontró una verificación para esta solicitud');
                }
            },
            onError: (error: any) => {
                setLoading(false);
                toast.error(error.message || 'Error al cargar los datos de verificación');
            }
        });
    };

    const handleEdit = (goToStep?: number) => {
        if (verificationId) {
            const stepParam = goToStep !== undefined ? `&step=${goToStep}` : '';
            window.location.href = `/intranet/inapi-application?verification_id=${verificationId}${stepParam}`;
        }
    };

    const handleDownloadPower = () => {
        if (verificationId) {
            downloadPowerDocument(verificationId).catch((e: Error) => toast.error(e.message));
        }
    };

    const handleCopySignatureLink = () => {
        if (!verificationId) return;

        generateSignatureLink({ verification_id: verificationId }, {
            onSuccess: async (response) => {
                const url = response.data?.signature_url;
                if (!url) {
                    toast.error('No se pudo obtener el enlace de firma');
                    return;
                }
                try {
                    await navigator.clipboard.writeText(url);
                    toast.success('Enlace copiado. El cliente puede firmar sin crear cuenta.');
                } catch {
                    // navigator.clipboard no existe fuera de HTTPS/localhost.
                    window.prompt('Copia el enlace de firma:', url);
                }
            },
            onError: (response) => {
                toast.error(response.message || 'No se pudo generar el enlace de firma');
            }
        });
    };

    const handleSendSignatureEmail = () => {
        if (!verificationId) return;

        sendSignatureEmail({ verification_id: verificationId }, {
            onSuccess: (response) => {
                toast.success(response.message || 'Correo enviado al solicitante');
            },
            onError: (response) => {
                toast.error(response.message || 'No se pudo enviar el correo');
            }
        });
    };

    const handleConfirmApplication = () => {
        if (!verificationId) return;
        
        requestConfirmation({
            title: '\u00bfConfirmar solicitud INAPI?',
            message: 'Al confirmar, se notificar\u00e1 al abogado asignado para que proceda con la presentaci\u00f3n ante INAPI. Esta acci\u00f3n no se puede deshacer.',
            buttons: {
                confirmButton: {
                    label: 'S\u00ed, confirmar',
                    onClick: () => {
                        confirmInapiApplication(
                            { verification_id: verificationId },
                            {
                                onSuccess: (response: any) => {
                                    toast.success(response.message || 'Solicitud confirmada exitosamente. El abogado ha sido notificado.');
                                    if (onUpdate) onUpdate();
                                },
                                onError: (error: any) => {
                                    toast.error(error.message || 'Error al confirmar la solicitud');
                                }
                            }
                        );
                    }
                },
                cancelButton: {
                    label: 'Cancelar',
                    onClick: () => { return; }
                }
            }
        });
    };


    const handleSaveInapiNumber = () => {
        if (!inapiData || !inapiNumber.trim()) {
            toast.error('Por favor ingrese el número de solicitud INAPI');
            return;
        }

        // Preparar datos como objeto - useFetch manejará la conversión a FormData si hay archivos
        const requestData: any = {
            inapi_application_id: inapiData.id,
            inapi_application_number: inapiNumber.trim()
        };
        
        if (submissionDocument) {
            requestData.inapi_submission_document = submissionDocument;
        }

        updateInapiNumber(requestData, {
            onSuccess: (response: any) => {
                toast.success(response.message || 'Número de solicitud INAPI actualizado exitosamente');
                setIsEditingNumber(false);
                setSubmissionDocument(null);
                if (onUpdate) {
                    onUpdate();
                }
                loadInapiData();
            },
            onError: (error: any) => {
                toast.error(error.message || 'Error al actualizar el número de solicitud INAPI');
            },
            onFieldError: (error: any) => {
                setErrors(error.data || {});
                toast.error('Por favor corrija los errores en el formulario');
            }
        });
    };

    const handleCancelEdit = () => {
        setInapiNumber(inapiData?.inapi_application_number || '');
        setIsEditingNumber(false);
        setSubmissionDocument(null);
        setErrors({});
    };
    
    const handleEditNumber = () => {
        setIsEditingNumber(true);
        setInapiNumber(inapiData?.inapi_application_number || '');
    };

    // Función para obtener el ícono según el tipo de archivo (desde URL)
    const getFileIcon = (fileUrl: string): string => {
        const extension = fileUrl.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'pdf':
                return 'fa-file-pdf';
            case 'doc':
            case 'docx':
                return 'fa-file-word';
            case 'jpg':
            case 'jpeg':
            case 'png':
                return 'fa-file-image';
            default:
                return 'fa-file';
        }
    };

    // Función para obtener el color del ícono según el tipo de archivo (desde URL)
    const getFileColor = (fileUrl: string): string => {
        const extension = fileUrl.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'pdf':
                return '#dc3545'; // Rojo
            case 'doc':
            case 'docx':
                return '#2b579a'; // Azul Word
            case 'jpg':
            case 'jpeg':
            case 'png':
                return '#28a745'; // Verde
            default:
                return '#6c757d'; // Gris
        }
    };

    if (loading || fetchingGetApplication) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                </div>
                <p className="mt-2 text-muted">Cargando datos de solicitud INAPI...</p>
            </div>
        );
    }

    if (!inapiData) {
        return (
            <div 
                className="border rounded-3 p-4 mb-4"
                style={{
                    backgroundColor: '#ffffff',
                    borderColor: '#dee2e6'
                }}
            >
                <div className="d-flex align-items-start justify-content-between">
                    <div className="flex-grow-1">
                        <div className="d-flex align-items-start mb-3">
                            <div 
                                className="rounded-circle d-flex align-items-center justify-content-center me-3"
                                style={{
                                    backgroundColor: '#ffc107',
                                    width: '48px',
                                    height: '48px',
                                    flexShrink: 0
                                }}
                            >
                                <i className="fas fa-exclamation-triangle text-white" style={{ fontSize: '1.3rem' }}></i>
                            </div>
                            <div>
                                <h5 className="mb-2" style={{ fontWeight: '600', color: '#212529' }}>
                                    Solicitud INAPI no iniciada
                                </h5>
                                <p className="mb-3 text-muted" style={{ fontSize: '0.95rem' }}>
                                    {verificationData?.payment_status === 'paid'
                                        ? 'El pago ha sido completado. Ya puedes iniciar la solicitud INAPI.'
                                        : stagesData?.summary.payment_managed_by_firm
                                            ? (stagesData.summary.is_applicant
                                                ? 'El pago de este trámite lo acuerdas directamente con tu estudio.'
                                                : 'Pago acordado internamente con el cliente. Márcalo como pagado en la Etapa 2 para iniciar.')
                                        : verificationData?.internal_payment_agreement
                                            ? 'Pago acordado internamente. Puedes iniciar cuando el abogado marque como pagado.'
                                            : 'Aún no se ha completado el pago. Debe completar el pago antes de continuar.'}
                                </p>
                                
                                {/* Información adicional */}
                                <div className="d-flex flex-column gap-2">
                                    {verificationData?.internal_payment_agreement && !stagesData?.summary.payment_managed_by_firm && (
                                        <div className="d-flex align-items-center">
                                            <i className="fas fa-handshake me-2 text-primary" style={{ fontSize: '1rem' }}></i>
                                            <span style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                                                Pago acordado internamente: El abogado y el cliente acordaron el pago sin pasar por la pasarela.
                                            </span>
                                        </div>
                                    )}
                                    
                                    <div className="d-flex align-items-center">
                                        <i 
                                            className={`fas ${verificationData?.payment_status === 'paid' ? 'fa-check-circle text-success' : 'fa-clock text-warning'} me-2`}
                                            style={{ fontSize: '1rem' }}
                                        ></i>
                                        <span style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                                            Estado de pago: <strong style={{ color: '#212529' }}>
                                                {verificationData?.payment_status === 'paid' 
                                                    ? 'Pagado' 
                                                    : verificationData?.payment_status === 'pending'
                                                        ? 'Pendiente'
                                                        : 'Sin definir'}
                                            </strong>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {verificationId && verificationData?.payment_status === 'paid' && (
                        <ButtonGeneral
                            type="button"
                            variant="primary"
                            size="medium"
                            onClick={handleEdit}
                            title="Iniciar Solicitud INAPI"
                            icon="fas fa-plus"
                        />
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="inapi-application-view">
            {/* Header con acciones */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="mb-1">
                        <i className="fas fa-file-contract me-2 text-primary"></i>
                        Solicitud INAPI
                    </h4>
                    <p className="text-muted mb-0">
                        Estado: <span className={`badge ${inapiData.status === 'submitted' ? 'bg-success' : 'bg-warning'}`}>
                            {inapiData.status === 'submitted' ? 'Enviada' : 'Borrador'}
                        </span>
                    </p>
                </div>
                {isEditable && (
                    <ButtonGeneral
                        type="button"
                        variant="primary"
                        size="medium"
                        onClick={handleEdit}
                        title="Editar Solicitud"
                        icon="fas fa-edit"
                    />
                )}
            </div>

            {/* Datos del Solicitante */}
            <div className="card mb-4">
                <div className="card-header bg-light">
                    <h5 className="mb-0">
                        <i className="fas fa-user me-2"></i>
                        Datos del Solicitante
                    </h5>
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label className="text-muted small">Tipo de Persona</label>
                            <p className="mb-0 fw-bold">{inapiData.person_type === 'juridica' ? 'Persona Jurídica' : 'Persona Natural'}</p>
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="text-muted small">Nombre Completo (Representante Legal)</label>
                            <p className="mb-0 fw-bold">
                                {`${inapiData.legal_representative_first_name} ${inapiData.legal_representative_paternal_last_name} ${inapiData.legal_representative_maternal_last_name}`}
                            </p>
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="text-muted small">RUT Representante</label>
                            <p className="mb-0">{inapiData.legal_representative_rut || '-'}</p>
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="text-muted small">Email</label>
                            <p className="mb-0">{inapiData.email || '-'}</p>
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="text-muted small">Teléfono</label>
                            <p className="mb-0">{inapiData.phone || '-'}</p>
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="text-muted small">Nacionalidad</label>
                            <p className="mb-0">{inapiData.nationality_country || '-'}</p>
                        </div>
                        {inapiData.person_type === 'juridica' && (
                            <>
                                <div className="col-md-6 mb-3">
                                    <label className="text-muted small">Razón Social</label>
                                    <p className="mb-0 fw-bold">{inapiData.company_name || '-'}</p>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="text-muted small">RUT Empresa</label>
                                    <p className="mb-0">{inapiData.company_rut || '-'}</p>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Dirección */}
            <div className="card mb-4">
                <div className="card-header bg-light">
                    <h5 className="mb-0">
                        <i className="fas fa-map-marker-alt me-2"></i>
                        Dirección
                    </h5>
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label className="text-muted small">País</label>
                            <p className="mb-0">{inapiData.residence_country_name || '-'}</p>
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="text-muted small">Ciudad/Región</label>
                            <p className="mb-0">
                                {[inapiData.residence_city, inapiData.residence_region_name]
                                    .filter(Boolean).join(', ') || '-'}
                            </p>
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="text-muted small">Comuna</label>
                            <p className="mb-0">{inapiData.residence_commune_name || '-'}</p>
                        </div>
                        <div className="col-md-8 mb-3">
                            <label className="text-muted small">Dirección</label>
                            <p className="mb-0">{inapiData.address || '-'}</p>
                        </div>

                    </div>
                </div>
            </div>

            {/* Poder Simple */}
            <div className="card mb-4">
                <div className="card-header bg-light">
                    <h5 className="mb-0">
                        <i className="fas fa-file-signature me-2"></i>
                        Poder Simple
                    </h5>
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label className="text-muted small">Ciudad</label>
                            <p className="mb-0">{inapiData.power_city || '-'}</p>
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="text-muted small">Fecha</label>
                            <p className="mb-0">
                                {inapiData.power_day && inapiData.power_month && inapiData.power_year
                                    ? `${inapiData.power_day}/${inapiData.power_month}/${inapiData.power_year}`
                                    : '-'}
                            </p>
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="text-muted small">Domicilio</label>
                            <p className="mb-0">{inapiData.power_address || '-'}</p>
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="text-muted small">Comuna</label>
                            <p className="mb-0">{inapiData.power_municipality || '-'}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Documentos */}
            <div className="card">
                <div className="card-header bg-light">
                    <h5 className="mb-0">
                        <i className="fas fa-paperclip me-2"></i>
                        Documentos
                    </h5>
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-12 mb-3">
                            <div className="d-flex align-items-center">
                                <i className={`fas fa-file-pdf me-2 ${inapiData.power_document_url ? 'text-danger' : 'text-muted'}`} style={{ fontSize: '1.5rem' }}></i>
                                <div className="flex-grow-1">
                                    <label className="text-muted small mb-0">Poder Simple Firmado</label>
                                    <p className="mb-0">
                                        {inapiData.power_document_url ? (
                                            <span className="text-success">
                                                <i className="fas fa-check-circle me-1"></i>
                                                Documento firmado digitalmente
                                            </span>
                                        ) : (
                                            <span className="text-warning">
                                                <i className="fas fa-clock me-1"></i>
                                                Pendiente de firma
                                            </span>
                                        )}
                                    </p>
                                </div>
                                {inapiData.power_document_url && (
                                    <div className="d-flex gap-2">
                                        <ButtonGeneral
                                            type="button"
                                            variant="outline"
                                            size="small"
                                            onClick={handleDownloadPower}
                                            title="Descargar PDF"
                                            icon="fas fa-download"
                                        />
                                        {canConfirm && (
                                            <ButtonGeneral
                                                type="button"
                                                variant="primary"
                                                size="small"
                                                onClick={() => handleEdit(1)}
                                                title="Firmar nuevamente"
                                                icon="fas fa-redo"
                                            />
                                        )}
                                    </div>
                                )}
                                {!inapiData.power_document_url && canSign && (
                                    <ButtonGeneral
                                        type="button"
                                        variant="primary"
                                        size="small"
                                        onClick={() => handleEdit(1)}
                                        title="Firmar Poder Simple"
                                        icon="fas fa-file-signature"
                                    />
                                )}
                                {/* El abogado puede pedirle la firma al cliente sin que éste
                                    tenga que iniciar sesión ni completar el formulario. */}
                                {!inapiData.power_document_url && !canSign && isEditable && (
                                    <div className="d-flex gap-2">
                                        <ButtonGeneral
                                            type="button"
                                            variant="outline"
                                            size="small"
                                            onClick={handleCopySignatureLink}
                                            disabled={fetchingSignatureLink}
                                            title="Copiar link de firma"
                                            icon="fas fa-link"
                                        />
                                        <ButtonGeneral
                                            type="button"
                                            variant="primary"
                                            size="small"
                                            onClick={handleSendSignatureEmail}
                                            disabled={fetchingSendSignatureEmail}
                                            title="Enviar correo para firmar"
                                            icon="fas fa-paper-plane"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Mensaje informativo para el solicitante cuando debe firmar */}
                    {!inapiData.power_document_url && canSign && (
                        <div className="alert alert-info border-primary mt-3 mb-0">
                            <div className="d-flex align-items-start">
                                <i className="fas fa-info-circle me-2 mt-1 text-primary"></i>
                                <div>
                                    <strong>Acción requerida</strong>
                                    <p className="mb-0 mt-1 small">
                                        Debe firmar el poder simple para continuar con el proceso de registro de marca.
                                        Haga clic en &ldquo;Firmar Poder Simple&rdquo; para generar y firmar el documento digitalmente.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Botón de confirmar para el solicitante después de firmar */}
            {canConfirm && (
                <div className="card mt-4" style={{ border: '2px solid #198754' }}>
                    <div 
                        className="card-header"
                        style={{ backgroundColor: '#198754', color: 'white' }}
                    >
                        <h5 className="mb-0" style={{ color: 'white' }}>
                            <i className="fas fa-check-double me-2"></i>
                            Confirmar solicitud
                        </h5>
                    </div>
                    <div className="card-body">
                        <div className="d-flex align-items-start">
                            <div 
                                className="rounded-circle d-flex align-items-center justify-content-center me-3"
                                style={{
                                    backgroundColor: '#d1e7dd',
                                    width: '48px',
                                    height: '48px',
                                    flexShrink: 0
                                }}
                            >
                                <i className="fas fa-file-signature text-success" style={{ fontSize: '1.3rem' }}></i>
                            </div>
                            <div className="flex-grow-1">
                                <p className="mb-2" style={{ fontSize: '0.95rem' }}>
                                    El poder simple ha sido firmado correctamente. Al confirmar, se notificar&aacute; al abogado asignado 
                                    para que proceda con la presentaci&oacute;n de la solicitud ante INAPI.
                                </p>
                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <i className="fas fa-check-circle text-success"></i>
                                    <span className="small text-success fw-bold">Poder simple firmado</span>
                                </div>
                                <ButtonGeneral
                                    type="button"
                                    variant="primary"
                                    size="medium"
                                    onClick={handleConfirmApplication}
                                    title="Confirmar y notificar al abogado"
                                    icon="fas fa-paper-plane"
                                    loading={fetchingConfirmApplication}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Sección de Número de Solicitud INAPI - Solo visible si está submitted y es editable */}
            {inapiData.status === 'submitted' && isEditable && (
                <DefaultCard>
                    <div 
                        style={{ 
                            padding: '1.5rem',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '0.5rem',
                            border: '2px solid #001849',
                            marginTop: '1.5rem'
                        }}
                    >
                        {/* Header destacado */}
                        <div 
                            style={{ 
                                padding: '1rem 1.5rem',
                                backgroundColor: '#001849',
                                color: 'white',
                                borderRadius: '0.5rem 0.5rem 0 0',
                                margin: '-1.5rem -1.5rem 1.5rem -1.5rem'
                            }}
                        >
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                    <i className="fas fa-exclamation-circle me-2" style={{ fontSize: '1.2rem', color: 'white' }}></i>
                                    <h5 className="mb-0" style={{ fontSize: '1.1rem', fontWeight: '600', color: 'white' }}>
                                        Registrar Número de Solicitud INAPI
                                    </h5>
                                </div>
                                {inapiData.inapi_application_number && !isEditingNumber && (
                                    <span 
                                        className="badge" 
                                        style={{ 
                                            backgroundColor: '#28a745',
                                            fontSize: '0.875rem',
                                            padding: '0.5rem 1rem'
                                        }}
                                    >
                                        <i className="fas fa-check-circle me-1"></i>
                                        Registrado
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Mensaje informativo */}
                        <div 
                            className="alert alert-info mb-4" 
                            style={{ 
                                borderLeft: '4px solid #001849',
                                backgroundColor: '#e3f2fd'
                            }}
                        >
                            <div className="d-flex align-items-start">
                                <i className="fas fa-info-circle me-2 mt-1" style={{ color: '#001849' }}></i>
                                <div>
                                    <strong style={{ color: '#001849' }}>Instrucciones:</strong>
                                    <ul className="mb-0 mt-2" style={{ paddingLeft: '1.2rem' }}>
                                        <li>Una vez que hayas presentado la solicitud en INAPI, recibirás un <strong>número de solicitud</strong>.</li>
                                        <li>Ingresa ese número aquí para continuar con el proceso y habilitar la <strong>Etapa 4: Examen de Forma</strong>.</li>
                                        <li>El número de solicitud INAPI es necesario para el seguimiento del proceso.</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Formulario de número INAPI */}
                        {isEditingNumber || !inapiData.inapi_application_number ? (
                            <div>
                                <InputField
                                    label="Número de Solicitud INAPI"
                                    value={inapiNumber}
                                    onChange={(value) => {
                                        setInapiNumber(value);
                                        onFocusRemove('inapi_application_number');
                                    }}
                                    onFocus={() => onFocusRemove('inapi_application_number')}
                                    error={fieldErrorMessage('inapi_application_number')}
                                    placeholder="Ej: 1634377"
                                    required
                                    icon="fa-hashtag"
                                />
                                
                                <div className="mt-3">
                                    <label className="form-label fw-bold">
                                        <i className="fas fa-file me-2"></i>
                                        Solicitud de marca presentada por internet
                                    </label>
                                    <FileUpload
                                        currentFile={submissionDocument}
                                        onFileSelect={(file) => {
                                            setSubmissionDocument(file);
                                            onFocusRemove('inapi_submission_document');
                                        }}
                                        acceptedTypes=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                        maxSize={10}
                                        showPreview={true}
                                    />
                                    <small className="text-muted d-block mt-1">
                                        Sube el documento de la solicitud presentada en INAPI (PDF, Word, JPG, PNG - máximo 10MB)
                                    </small>
                                    {fieldErrorMessage('inapi_submission_document') && (
                                        <div className="text-danger small mt-1">
                                            {fieldErrorMessage('inapi_submission_document')}
                                        </div>
                                    )}
                                </div>
                                
                                <div className="d-flex gap-2 mt-3">
                                    <ButtonGeneral
                                        type="button"
                                        variant="primary"
                                        size="medium"
                                        onClick={handleSaveInapiNumber}
                                        title="Guardar Número"
                                        icon="fa-save"
                                        loading={fetchingUpdateInapiNumber}
                                    />
                                    {inapiData.inapi_application_number && (
                                        <ButtonGeneral
                                            type="button"
                                            variant="secondary"
                                            size="medium"
                                            onClick={handleCancelEdit}
                                            title="Cancelar"
                                            icon="fa-times"
                                        />
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div 
                                    style={{ 
                                        padding: '1rem',
                                        backgroundColor: '#e8f5e9',
                                        border: '1px solid #4caf50',
                                        borderRadius: '0.5rem',
                                        marginBottom: '1rem'
                                    }}
                                >
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <div>
                                            <label className="text-muted small mb-1 d-block">Número de Solicitud INAPI Registrado:</label>
                                            <p className="mb-0" style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1b5e20' }}>
                                                <i className="fas fa-hashtag me-2"></i>
                                                {inapiData.inapi_application_number}
                                            </p>
                                        </div>
                                        <ButtonGeneral
                                            type="button"
                                            variant="primary"
                                            size="medium"
                                            onClick={handleEditNumber}
                                            title="Editar"
                                            icon="fa-edit"
                                        />
                                    </div>
                                    
                                    {inapiData.inapi_submission_document_url && (
                                        <div className="d-flex align-items-center justify-content-between pt-3" style={{ borderTop: '1px solid #4caf50' }}>
                                            <div className="d-flex align-items-center">
                                                <i 
                                                    className={`fas ${getFileIcon(inapiData.inapi_submission_document_url)} me-2`} 
                                                    style={{ fontSize: '1.5rem', color: getFileColor(inapiData.inapi_submission_document_url) }}
                                                ></i>
                                                <div>
                                                    <label className="text-muted small mb-0 d-block">Solicitud presentada en INAPI</label>
                                                    <span className="text-success small">
                                                        <i className="fas fa-check-circle me-1"></i>
                                                        Documento adjunto
                                                    </span>
                                                </div>
                                            </div>
                                            <ButtonGeneral
                                                type="button"
                                                variant="outline"
                                                size="small"
                                                onClick={() => window.open(inapiData.inapi_submission_document_url || '', '_blank')}
                                                title="Ver documento"
                                                icon="fa-eye"
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="alert alert-success mb-0" style={{ display: 'block' }}>
                                    <div className="d-flex align-items-start">
                                        <i className="fas fa-check-circle me-2 mt-1"></i>
                                        <div>
                                            <strong>¡Perfecto!</strong> El número de solicitud INAPI ha sido registrado. 
                                            <br />
                                            Ahora puedes continuar con la <strong>Etapa 4: Examen de Forma</strong>.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </DefaultCard>
            )}
        </div>
    );
};

export default InapiApplicationStep;

